<template>
  <Card :title="cardTitle">
    <div v-if="isEditMode">
      <MemberActivityParticipantTable
          :activity-registration="selectedActivityRegistration"
          :is-less-than-two-days="isLessThanTwoDays"
          :participants="participants"
          @delete-participant="deleteParticipant"
      />
      <button
          class="btn btn--fullscreen"
          type="button"
          @click="isEditMode = !isEditMode"
      > {{ t("global.back") }}
      </button>
    </div>
    <div v-else>
      <MemberActivityTable @activityRegistrationSelected="handleEdit"/>
    </div>
    <FormTooltip class="util--with-top-margin">
      <p><strong>{{ t('registrations.deleteMemberRegistration--tooltip') }}</strong></p>
    </FormTooltip>
  </Card>
</template>

<script lang="ts" setup>
import {useI18n} from "vue3-i18n";
import FormTooltip from "@/components/forms/FormTooltip.vue";
import Card from '@/components/layout/Card.vue';
import MemberActivityParticipantTable from "@/components/tables/MemberActivityParticipantTable.vue";

import {useMemberService} from "@/inversify.config";
import {computed, onMounted, ref} from "vue";
import {ActivityRegistration, Participant} from "@/types";
import {notifyError, notifySuccess} from "@/notify";
import {SucceededOrNotResponse} from "@/types/responses";
import MemberActivityTable from "@/components/tables/MemberActivityTable.vue";

const {t} = useI18n();
const memberService = useMemberService()

const cardTitle = computed(() => isEditMode.value ? t('participants.participants') : t('registrations.registrations'))
const isEditMode = ref(false)

const activityRegistrations = ref<ActivityRegistration[]>([])
const selectedActivityRegistration = ref<ActivityRegistration>()
const participants = ref<Participant[]>([])
const isLessThanTwoDays = ref(false)

onMounted(async () => {
  await fetchActivityRegistration();
})

async function fetchActivityRegistration() {
  let response = await memberService.getCurrentMemberActivityRegistrations()
  activityRegistrations.value = response as ActivityRegistration[];
}

function handleEdit(id: string) {
  let activityRegistration = activityRegistrations.value.find(x => x.id == id)
  selectedActivityRegistration.value = activityRegistration;
  participants.value = activityRegistration?.participants ?? []
  isLessThanTwoDays.value = new Date(activityRegistration?.activityOccurrenceStartDateAndTime ?? "").hoursBetweenToday() < 48
  isEditMode.value = true
}

async function deleteParticipant(participantId: string) {
  let result: SucceededOrNotResponse = await memberService
    .deleteCurrentMemberActivityRegistrationParticipant(selectedActivityRegistration.value?.id ?? "", participantId);

  if (result?.succeeded) {
    notifySuccess(t("participants.form.deleteSuccess"));
    await fetchActivityRegistration();
    let activityRegistration = activityRegistrations.value.find(x => x.id == selectedActivityRegistration.value?.id ?? "")
    participants.value = activityRegistration?.participants ?? []
    isEditMode.value = participants.value.length > 0 // go back to all registrations if no participant is left
  } else {
    notifyError(t("participants.form.deleteError"));
  }
}
</script>